import m from 'mithril';
import { padLeft, uniqueId } from 'mithril-materialized';
import { render } from 'mithril-ui-form';
import { diagnoseGeneration, generateCombination, setInconsistency } from 'morphological-box';
import {
  Color,
  ContextType,
  DataModel,
  ID,
  Inconsistencies,
  Narrative,
  OldDataModel,
  OsmTypeList,
  Scenario,
  ScenarioComponent,
  ThresholdColor,
  thresholdColors,
} from '../models';
import { t } from '../services';
import type { ComparisonExport } from './decision';
import { htmlToMarkdown, looksLikeHtml } from './html-to-markdown';

export * from './decision';
export * from './template-markdown';
export { htmlToMarkdown, looksLikeHtml };
export { clipboardHtmlToMarkdown } from './clipboard-markdown';

export const LANGUAGE = 'SG_LANGUAGE';
export const SAVED = 'SG_MODEL_SAVED';

const supRegex = /\^([^_ ]+)(_|$|\s)/g;
const subRegex = /\_([^\^ ]+)(\^|$|\s)/g;

/** Expand markdown notation by converting A_1 to subscript and x^2 to superscript. */
export const subSup = (s: string) =>
  s
    ? s
        ?.toString()
        .replace(supRegex, `<sup>$1</sup>`)
        .replace(subRegex, `<sub>$1</sub>`)
    : s;

export const capitalize = (s?: string) =>
  s && s.length > 0 ? s.charAt(0).toUpperCase() + s.slice(1) : s;

/**
 * Debounce function wrapper, i.e. between consecutive calls of the wrapped function,
 * there will be at least TIMEOUT milliseconds.
 * @param func Function to execute
 * @param timeout Timeout in milliseconds
 * @returns
 */
export const debounce = (func: (...args: any) => void, timeout: number) => {
  let timer: number;
  return (...args: any) => {
    clearTimeout(timer);
    timer = window.setTimeout(() => {
      func(...args);
    }, timeout);
  };
};

export const formatDate = (date: number | Date = new Date()) => {
  const d = new Date(date);
  return `${d.getFullYear()}-${padLeft(d.getMonth() + 1)}-${padLeft(
    d.getDate(),
  )}`;
};

/**
 * Get a color that is clearly visible against a background color
 * @param backgroundColor Background color, e.g. #99AABB
 * @returns
 */
export const getTextColorFromBackground = (backgroundColor?: string) => {
  if (!backgroundColor) {
    return 'black-text';
  }
  const c = backgroundColor.substring(1); // strip #
  const rgb = parseInt(c, 16); // convert rrggbb to decimal
  const r = (rgb >> 16) & 0xff; // extract red
  const g = (rgb >> 8) & 0xff; // extract green
  const b = (rgb >> 0) & 0xff; // extract blue

  const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b; // per ITU-R BT.709

  return luma < 105 ? 'white-text' : 'black-text';
};

type Option<T> = {
  id: T;
  label: string;
  title?: string;
};

export const getOptionsLabel = <T>(
  options: Array<Option<T>>,
  id?: T | T[],
  showTitle = true,
) => {
  if (!id) {
    return '';
  }
  const print = (o: Option<T>) =>
    showTitle
      ? `${o.label}${o.title ? ` (${o.title.toString().replace(/\.\s*$/, '')})` : ''}`
      : o.label;
  if (id instanceof Array) {
    return options
      .filter((o) => id.indexOf(o.id) >= 0)
      .map((o) => print(o))
      .join(', ');
  }
  const found = options.filter((o) => o.id === id).shift();
  return found ? print(found) : '';
};

/** Join a list of items with a comma, and use AND for the last item in the list. */
export const joinListWithAnd = (
  arr: string[] = [],
  and = 'and',
  prefix = '',
  lowercase = true,
) => {
  const terms = arr.filter((term) => term);
  return terms.length === 0
    ? ''
    : prefix +
        (terms.length === 1
          ? terms[0]
          : `${terms
              .slice(0, terms.length - 1)
              .map((t, i) =>
                i === 0 || typeof t === 'undefined' || !lowercase
                  ? t
                  : t.toLowerCase(),
              )
              .join(', ')} ${and} ${
              lowercase
                ? terms[terms.length - 1].toLowerCase()
                : terms[terms.length - 1]
            }`);
};

/** Convert markdown text to HTML */
export const markdown2html = (markdown = '') =>
  m.trust(render(markdown, true, true));

export const isUnique = <T>(item: T, pos: number, arr: T[]) =>
  arr.indexOf(item) == pos;

/** Generate an array of numbers, from start till end, with optional step size. */
export const generateNumbers = (
  start: number,
  end: number,
  step: number = 1,
): number[] => {
  if (start > end) {
    throw new Error(
      'Start number must be less than or equal to the end number.',
    );
  }

  if (step <= 0) {
    throw new Error('Step size must be a positive number.');
  }

  const length = Math.floor((end - start) / step) + 1;
  return Array.from({ length }, (_, index) => start + index * step);
};

export const getRandomValue = <T>(array: T[]): T | undefined => {
  if (array.length === 0) {
    return undefined;
  }

  const randomIndex = Math.floor(Math.random() * array.length);
  return array[randomIndex];
};

/**
 * Deep copy function for TypeScript.
 * @param T Generic type of target/copied value.
 * @param target Target value to be copied.
 * @see Source project, ts-deepcopy https://github.com/ykdr2017/ts-deepcopy
 * @see Code pen https://codepen.io/erikvullings/pen/ejyBYg
 */
export const deepCopy = <T>(target: T): T => {
  if (target === null) {
    return target;
  }
  if (target instanceof Date) {
    return new Date(target.getTime()) as any;
  }
  if (target instanceof Array) {
    const cp = [] as any[];
    (target as any[]).forEach((v) => {
      cp.push(v);
    });
    return cp.map((n: any) => deepCopy<any>(n)) as any;
  }
  if (typeof target === 'object') {
    const cp = { ...(target as { [key: string]: any }) } as {
      [key: string]: any;
    };
    Object.keys(cp).forEach((k) => {
      cp[k] = deepCopy<any>(cp[k]);
    });
    return cp as T;
  }
  return target;
};

export const convertFromOld = (old: OldDataModel): DataModel => {
  return Object.keys(old).reduce(
    (acc, cur) => {
      if (cur === 'scenarios') {
        // Parse scenarios
        const scenario = old[cur].current;
        acc.scenario.id = scenario.id;
        acc.scenario.label = scenario.name;
        acc.scenario.desc = scenario.desc;
        acc.scenario.hideInconsistentValues = true;
        acc.scenario.inconsistencies = scenario.inconsistencies.reduce(
          (acc, cur) => {
            const {
              ids: [from, to],
              type,
            } = cur;
            return setInconsistency(
              acc,
              from,
              to,
              type === 'totally' ? 'impossible' : 'unlikely',
            );
          },
          {} as Inconsistencies,
        );
        acc.scenario.narratives = scenario.narratives.map(
          ({ id, name, components, narrative, included }) => ({
            id,
            label: name,
            components: Object.keys(components).reduce(
              (acc, key) => {
                acc[key] = [components[key]];
                return acc;
              },
              {} as { [key: ID]: ID[] },
            ),
            desc: narrative,
            included,
            saved: true,
            personaEffects: {},
          }),
        );
        acc.scenario.categories = Object.keys(scenario.categories).map(
          (key) => ({
            id: key,
            label: key,
            componentIds: scenario.categories[key],
          }),
        );
      } else {
        // Parse components
        if (!acc.scenario.components) acc.scenario.components = [];
        const componentValues = old[cur].list;
        const contexts = componentValues.reduce((acc, cur) => {
          if (cur.context && cur.context.type) {
            if (cur.context.type === 'LOCATION' && acc.indexOf('location') <= 0)
              acc.push('location');
            else if (
              cur.context.type === 'LOCATIONTYPE' &&
              acc.indexOf('locationType') <= 0
            )
              acc.push('locationType');
          }
          return acc;
        }, [] as ContextType[]);
        acc.scenario.components.push({
          id: cur,
          label: t('COMP', cur.toUpperCase() as any),
          contexts,
          values: componentValues.map(({ name, id, desc, context }) => {
            const newContext = context
              ? context.type === 'LOCATION'
                ? 'location'
                : context.type === 'LOCATIONTYPE'
                  ? 'locationType'
                  : 'none'
              : undefined;
            const locationType =
              context && context.type === 'LOCATION'
                ? context.data.NAME
                  ? 'name'
                  : context.data.COORDINATES
                    ? 'coords'
                    : undefined
                : undefined;
            const [lat, lon] =
              locationType === 'coords' && context!.data.COORDINATES
                ? context!.data.COORDINATES.split(/,/).map((n) => +n)
                : [undefined, undefined];
            const locationTypeType =
              context && context.type === 'LOCATIONTYPE'
                ? OsmTypeList.indexOf(Object.keys(context.data).shift()) >= 0
                  ? 'list'
                  : 'keyValue'
                : undefined;
            const osmTypeId =
              locationTypeType === 'list'
                ? Object.keys(context!.data).shift()
                : undefined;
            const keyValue =
              locationTypeType === 'keyValue'
                ? Object.entries(context!.data).shift()
                : undefined;
            const [key, value] = keyValue || [undefined, undefined];
            return {
              id,
              label: name,
              desc,
              context: newContext,
              location:
                context && context.type === 'LOCATION'
                  ? context.data.NAME
                  : undefined,
              locationType,
              locationTypeType,
              lat,
              lon,
              osmTypeId,
              key,
              value,
            };
          }),
        });
        acc.scenario.thresholdColors = thresholdColors;
        if (acc.scenario.categories) {
          let order = 1;
          const compIds = acc.scenario.categories
            .filter((c) => c.componentIds)
            .reduce(
              (acc, cur) => {
                cur.componentIds?.forEach((c) => (acc[c] = order++));
                return acc;
              },
              {} as { [key: ID]: number },
            );
          acc.scenario.components = acc.scenario.components.map((c) => ({
            ...c,
            order: compIds[c.id],
          }));
          acc.scenario.components.sort((a, b) =>
            a.order! > b.order! ? 1 : -1,
          );
        }
      }
      return acc;
    },
    {
      scenario: {} as Scenario,
      version: 1,
      lastUpdate: Date.now(),
      scenarios: [],
    } as DataModel,
  );
};

export const modelToSaveName = (
  model: DataModel,
  narrativeName?: string,
  isCollection = true,
) => {
  let name = isCollection
    ? 'spark_collection'
    : (model.scenario?.label || 'spark') + '_model';
  if (narrativeName) {
    name += `_${narrativeName}`;
  }
  return `${name?.toString().replace(/\s/g, '_')}_v${padLeft(
    model.version || 1,
    3,
  )}_${formatDate()}`.toLowerCase();
};

export type { GenerationDiagnostic } from 'morphological-box';
export { diagnoseGeneration };

/**
 * Generate a narrative without impossible combinations and with as few
 * unlikely combinations as possible, keeping the locked values.
 */
export const generateNarrative = (
  scenario: Scenario,
  locked: Record<ID, ID[]> = {},
) => {
  const result = generateCombination(scenario, { locked });
  if (!result.ok) return { error: true, diagnostic: result.diagnostic } as const;
  return {
    id: uniqueId(),
    components: result.selection,
    included: false,
  } as Narrative;
};

export const scrollToSection = (id: string, e?: MouseEvent): void => {
  e && e.preventDefault();
  const element = document.getElementById(id);

  if (element) {
    element.scrollIntoView({ behavior: 'smooth' });
  } else {
    console.log(`Element with id ${id} not found.`);
  }
};

export const scrollToTop = (): void => {
  window.scrollTo({
    top: 0,
    behavior: 'smooth',
  });
};

export const validateNarrative = (
  n: Narrative,
  components: ScenarioComponent[],
) => {
  const validSelection = (selection: Narrative['components'] = {}) =>
    components
      .filter((c) => selection.hasOwnProperty(c.id))
      .reduce(
        (acc, c) => {
          acc[c.id] = (selection[c.id] || []).filter((id) =>
            c.values?.find((v) => v.id === id),
          );
          return acc;
        },
        {} as Narrative['components'],
      );
  const { components: narrativeComps, ...attrs } = n;
  const newNarrative = {
    ...attrs,
    components: validSelection(narrativeComps),
  } as Narrative;
  if (n.coas) {
    newNarrative.coas = n.coas.map((coa) => ({
      ...coa,
      components: validSelection(coa.components),
    }));
  }
  return newNarrative;
};

export const narrativesToOptions = (narratives: Narrative[]) =>
  narratives
    .map((n) => ({
      ...n,
      group: n.included ? t('GROUP', 'SELECTED') : t('GROUP', 'UNSELECTED'),
    }))
    .sort((a, b) =>
      a.included && b.included
        ? (a.label || '').localeCompare(b.label)
        : a.included
          ? -1
          : 1,
    );

export const trafficLight = [
  '#2c7bb6',
  '#abd9e9',
  '#ffffbf',
  '#fdae61',
  '#d7191c',
];

export const svgToDataURI = (svg: string): string => {
  const svgBase64 = btoa(svg); // Convert SVG string to base64

  return `data:image/svg+xml;base64,${svgBase64}`; // Construct data URI
};

export const createCircleSVG = (color: string, diameter: number): string => {
  const strokeWidth = diameter * 0.1; // Width of the border as 5% of the diameter

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${diameter}" height="${diameter}">
    <circle cx="${diameter / 2}" cy="${diameter / 2}" r="${
      (diameter - strokeWidth) / 2
    }" fill="${color}" stroke="black" stroke-width="${strokeWidth}" /></svg>`;

  return svg;
};

export const generateUniqueTitle = (
  title: string,
  otherTitles: string[] = [],
): string => {
  let count = 1;

  // Check if the original title ends with a number
  const match = title.match(/^(.*?)(\d+)$/);
  if (match) {
    // Extract the base title and the number
    title = match[1].trim();
    // Increment the number
    count = parseInt(match[2]) + 1;
  }
  // Construct the new title
  let newTitle = `${title} ${count}`;

  // Ensure the new title is unique
  while (otherTitles.includes(newTitle)) {
    count++;
    newTitle = `${title} ${count}`;
  }

  return newTitle;
};

type QuillOp = {
  insert: string;
  attributes?: {
    bold?: boolean;
    italic?: boolean;
    header?: number;
    list?: 'ordered' | 'bullet' | 'checked' | 'unchecked';
    indent?: number;
    link?: string;
    blockquote?: boolean;
  };
};

/** Convert a stored narrative description (Quill JSON, HTML or Markdown) to Markdown. */
export const narrativeToMarkdown = (desc?: string): string => {
  if (!desc) return '';
  const text = desc.toString();
  if (text.startsWith('{') || text.startsWith('[')) {
    try {
      return quillToMarkdown(JSON.parse(text));
    } catch {
      return text;
    }
  }
  return looksLikeHtml(text) ? htmlToMarkdown(text) : text;
};

/** True when the narrative has a (non-empty) written story. */
export const hasNarrativeText = (n: Pick<Narrative, 'desc'>) => narrativeToMarkdown(n.desc).trim().length > 0;

export const quillToMarkdown = (quillDelta: { ops: QuillOp[] }): string => {
  let md = '';
  let buffer = ''; // collect inline text until newline
  let listCounters: Record<number, number> = {}; // for nested ordered lists

  const flushParagraph = (text: string, attrs?: QuillOp['attributes']) => {
    if (!attrs) {
      md += text + '\n';
      return;
    }

    // Handle headers
    if (attrs.header) {
      md += `${'#'.repeat(attrs.header)} ${text.trim()}\n\n`;
      return;
    }

    // Handle blockquote
    if (attrs.blockquote) {
      const quoteLines = text
        .trim()
        .split('\n')
        .map((l) => `> ${l}`)
        .join('\n');
      md += `${quoteLines}\n\n`;
      return;
    }

    // Handle lists
    if (attrs.list) {
      const indent = attrs.indent || 0;
      const indentSpaces = '  '.repeat(indent);

      if (attrs.list === 'bullet') {
        md += `${indentSpaces}- ${text.trim()}\n`;
      } else if (attrs.list === 'ordered') {
        listCounters[indent] = (listCounters[indent] || 0) + 1;
        md += `${indentSpaces}${listCounters[indent]}. ${text.trim()}\n`;
      } else if (attrs.list === 'checked') {
        md += `${indentSpaces}- [x] ${text.trim()}\n`;
      } else if (attrs.list === 'unchecked') {
        md += `${indentSpaces}- [ ] ${text.trim()}\n`;
      }
      return;
    }

    // Default paragraph
    md += text + '\n\n';
  };

  for (const op of quillDelta.ops) {
    if (typeof op.insert !== 'string') continue;

    let text = op.insert;

    // Inline formatting
    if (op.attributes?.link) {
      text = `[${text.trim()}](${op.attributes.link})`;
    }
    if (op.attributes?.bold) {
      text = `**${text.trim()}**`;
    }
    if (op.attributes?.italic) {
      text = `*${text.trim()}*`;
    }

    // If newline with block attributes → flush buffer as that block type
    if (text === '\n' && op.attributes) {
      flushParagraph(buffer, op.attributes);
      buffer = '';
      continue;
    }

    // Normal newline (no special attributes) → treat as line break in paragraph
    if (text === '\n') {
      md += buffer + '\n\n';
      buffer = '';
      continue;
    }

    // Otherwise, accumulate text
    buffer += text;
  }

  // Flush any remaining text
  if (buffer) {
    md += buffer;
  }

  return md.trim();
};

/** Compute a contrasting text color (black or white) based on WCAG luminance */
export const contrastingColor = (() => {
  const cache = new Map<string, string>();

  return (hex: string): string => {
    if (cache.has(hex)) {
      return cache.get(hex)!;
    }

    // Expand shorthand #abc -> #aabbcc
    let c = hex?.toString().replace(/^#/, '').toLowerCase();
    if (c.length === 3) {
      c = c
        .split('')
        .map((ch) => ch + ch)
        .join('');
    }

    const r = parseInt(c.slice(0, 2), 16) / 255;
    const g = parseInt(c.slice(2, 4), 16) / 255;
    const b = parseInt(c.slice(4, 6), 16) / 255;

    // Convert to linear RGB
    const toLinear = (v: number) =>
      v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);

    const R = toLinear(r);
    const G = toLinear(g);
    const B = toLinear(b);

    // Relative luminance
    const L = 0.2126 * R + 0.7152 * G + 0.0722 * B;

    const result = L > 0.179 ? '#000000' : '#ffffff';
    cache.set(hex, result);
    return result;
  };
})();

// Utility: convert hex -> HSL
const hexToHsl = (hex: string): [number, number, number] => {
  hex = (hex || '').toString().replace(/^#/, '');
  if (hex.length === 3) {
    hex = hex
      .split('')
      .map((c) => c + c)
      .join('');
  }
  const r = parseInt(hex.slice(0, 2), 16) / 255;
  const g = parseInt(hex.slice(2, 4), 16) / 255;
  const b = parseInt(hex.slice(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0,
    s = 0,
    l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }
  return [h * 360, s * 100, l * 100];
};

// Utility: convert HSL -> hex
const hslToHex = (h: number, s: number, l: number): string => {
  s /= 100;
  l /= 100;

  const k = (n: number) => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) =>
    l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));

  const toHex = (x: number) =>
    Math.round(255 * x)
      .toString(16)
      .padStart(2, '0');

  return `#${toHex(f(0))}${toHex(f(8))}${toHex(f(4))}`;
};

/**
 * Transform a light-theme hex color to a dark-theme equivalent
 * - Keeps Hue and Saturation
 * - Flips Lightness around midpoint (e.g. 20% -> 80%)
 * - Memoized for performance
 */
export const toDarkThemeColor = (() => {
  const cache = new Map<string, string>();

  return (hex: string): string => {
    const key = hex.toLowerCase();
    if (cache.has(key)) {
      return cache.get(key)!;
    }

    const [h, s, l] = hexToHsl(key);
    const newL = 100 - l; // flip lightness
    const result = hslToHex(h, s, newL);

    cache.set(key, result);
    return result;
  };
})();

export const computeCompColor = (
  narratives: Scenario['narratives'] = [],
  thresholdColors: ThresholdColor[],
): { [key: ID]: [Color, Color] } => {
  if (!thresholdColors || thresholdColors.length === 0) {
    // no thresholds defined → everything gets a fallback color
    const fallback = '#ccc';
    return { OTHER: [fallback, contrastingColor(fallback)] };
  }

  // ensure thresholds are sorted without mutating original array
  const sortedThresholds = [...thresholdColors].sort(
    (a, b) => a.threshold - b.threshold,
  );

  // build usage counts
  const componentUsage = narratives
    .filter((n) => n.included)
    .reduce(
      (acc, cur) => {
        Object.keys(cur.components || {}).forEach((c) => {
          const vals = cur.components[c];
          if (!Array.isArray(vals)) return;
          for (const compValue of vals) {
            acc[compValue] = (acc[compValue] || 0) + 1;
          }
        });
        return acc;
      },
      {} as { [key: ID]: number },
    );

  // maximum threshold value
  const maxThreshold = sortedThresholds[sortedThresholds.length - 1].threshold;

  // map counts to colors (approach 1: last threshold reached)
  const count2color: Color[] = new Array(maxThreshold + 1);

  for (let count = 0; count <= maxThreshold; count++) {
    // find the highest threshold ≤ count
    let color = sortedThresholds[0].color;
    for (const tc of sortedThresholds) {
      if (tc.threshold <= count) {
        color = tc.color;
      } else {
        break;
      }
    }
    count2color[count] = color;
  }

  // assign colors to components
  const compColor = Object.entries(componentUsage).reduce(
    (acc, [id, count]) => {
      const color =
        count < count2color.length
          ? count2color[count]
          : sortedThresholds[sortedThresholds.length - 1].color;

      acc[id] = [color, contrastingColor(color)];
      return acc;
    },
    {} as { [key: ID]: [Color, Color] },
  );

  // add OTHER default
  const baseColor = count2color[0] || sortedThresholds[0].color;
  compColor.OTHER = [baseColor, contrastingColor(baseColor)];

  return compColor;
};

export const uploadFile = (onSelect: (files: FileList) => void): void => {
  // Create the input
  const input = document.createElement('input');
  input.type = 'file';
  input.style.display = 'none';

  // Attach onchange handler
  input.onchange = () => {
    if (input.files) {
      onSelect(input.files);
    }
    // Clean up
    input.remove();
  };

  // Add to DOM, trigger click, and schedule removal if cancelled
  document.body.appendChild(input);
  input.click();

  // If user cancels, still remove the input after a short delay
  setTimeout(() => input.remove(), 1000);
};

export const downloadAsWord = (
  htmlContent: string,
  filename = 'document.doc',
) => {
  // Wrap in Word-compatible HTML
  const preHtml =
    `<!DOCTYPE html><html xmlns:o="urn:schemas-microsoft-com:office:office" ` +
    `xmlns:w="urn:schemas-microsoft-com:office:word" lang="en"><head><meta charset="utf-8"></head><body>`;
  const postHtml = '</body></html>';
  const fullHtml = preHtml + htmlContent + postHtml;

  // Create Blob
  const blob = new Blob([fullHtml], {
    type: 'application/msword;charset=utf-8',
  });

  // Create object URL
  const url = URL.createObjectURL(blob);

  // Create temp link
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;

  // Trigger download
  document.body.appendChild(link);
  link.click();

  // Cleanup
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export type DocxBoxTable = {
  /** Category label, shown as a heading when present */
  label?: string;
  rows: Array<{
    label: string;
    values: Array<{ label: string; selected: boolean }>;
  }>;
};

export type DocxNarrative = {
  label: string;
  content: string;
  /** Optional morphological box tables (one per category) with the selected values highlighted */
  box?: DocxBoxTable[];
  /** Courses of action (selected decisions per COA), listed in their own section when present */
  coas?: Array<{
    label: string;
    desc?: string;
    notes?: string;
    decisions: Array<{ label: string; values: string[] }>;
    /** Already translated warnings, e.g. decisions that conflict with the situation */
    warnings?: string[];
    /** Already translated residual risk, e.g. "Residual risk: Low (situation: High)" */
    risk?: string;
    /** Reflection on the course of action (Markdown, often LLM-generated) */
    reflection?: string;
  }>;
};

export type DocxOptions = {
  /** Heading of a course-of-action reflection */
  reflectionTitle?: string;
  /** Decision analysis (as exported from the decision-support page), appended on landscape pages */
  analysis?: ComparisonExport;
};

/**
 * Generate a real .docx Word document from scenario narratives.
 * Creates headings, paragraphs, and a summary table.
 */
export const downloadAsDocx = async (
  title: string,
  narratives: DocxNarrative[],
  filename = 'scenario.docx',
  decisionsTitle = 'Decisions',
  options: DocxOptions = {},
): Promise<void> => {
  const sanitizeDocxText = (input: string): string =>
    input.replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, '');

  const {
    Document,
    Packer,
    Paragraph,
    TextRun,
    HeadingLevel,
    LevelFormat,
    AlignmentType,
    Table,
    TableRow,
    TableCell,
    WidthType,
    BorderStyle,
    PageBreak,
  } = await import('docx');

  /** Word paragraphs for Markdown text: headings, lists, rules, bold and italic */
  const markdownParagraphs = (text: string): import('docx').Paragraph[] => {
    const children: import('docx').Paragraph[] = [];
    const parseInlineRuns = (text: string): import('docx').TextRun[] => {
      const clean = sanitizeDocxText(text);
      const runs: import('docx').TextRun[] = [];
      const tokenRegex = /(\*\*[^*]+\*\*|__[^_]+__|\*[^*]+\*|_[^_]+_)/g;
      let last = 0;
      let match: RegExpExecArray | null;

      while ((match = tokenRegex.exec(clean)) !== null) {
        if (match.index > last) {
          runs.push(new TextRun(clean.slice(last, match.index)));
        }

        const token = match[0];
        const isStrong =
          (token.startsWith('**') && token.endsWith('**')) ||
          (token.startsWith('__') && token.endsWith('__'));
        const content = isStrong
          ? token.slice(2, -2)
          : token.slice(1, -1);

        runs.push(
          new TextRun({
            text: content,
            bold: isStrong,
            italics: !isStrong,
          }),
        );
        last = match.index + token.length;
      }

      if (last < clean.length) {
        runs.push(new TextRun(clean.slice(last)));
      }

      return runs.length > 0 ? runs : [new TextRun(clean)];
    };

    const paragraphBuffer: string[] = [];
    const flushParagraph = () => {
      if (paragraphBuffer.length === 0) return;
      const paragraphText = paragraphBuffer.join(' ').trim();
      if (paragraphText) {
        children.push(
          new Paragraph({
            children: parseInlineRuns(paragraphText),
            spacing: { after: 120 },
          }),
        );
      }
      paragraphBuffer.length = 0;
    };

    for (const rawLine of text.split('\n')) {
      const line = rawLine.trim();

      if (!line) {
        flushParagraph();
        continue;
      }

      const headingMatch = line.match(/^(#{1,6})\s+(.+)$/);
      if (headingMatch) {
        flushParagraph();
        const depth = headingMatch[1].length;
        const headingMap = {
          1: HeadingLevel.HEADING_1,
          2: HeadingLevel.HEADING_2,
          3: HeadingLevel.HEADING_3,
          4: HeadingLevel.HEADING_4,
          5: HeadingLevel.HEADING_5,
          6: HeadingLevel.HEADING_6,
        } as const;
        children.push(
          new Paragraph({
            text: sanitizeDocxText(headingMatch[2].trim()),
            heading:
              headingMap[Math.min(depth, 6) as 1 | 2 | 3 | 4 | 5 | 6],
            spacing: { before: 120, after: 80 },
          }),
        );
        continue;
      }

      if (/^([-*_])(?:\s*\1){2,}\s*$/.test(line)) {
        flushParagraph();
        children.push(
          new Paragraph({
            border: {
              bottom: {
                style: BorderStyle.SINGLE,
                size: 6,
                color: 'BDBDBD',
              },
            },
            spacing: { before: 120, after: 120 },
          }),
        );
        continue;
      }

      const unordered = line.match(/^[-*•]\s+(.+)$/);
      if (unordered) {
        flushParagraph();
        children.push(
          new Paragraph({
            children: parseInlineRuns(unordered[1].trim()),
            bullet: { level: 0 },
            spacing: { after: 60 },
          }),
        );
        continue;
      }

      const ordered = line.match(/^\d+[\.)]\s+(.+)$/);
      if (ordered) {
        flushParagraph();
        children.push(
          new Paragraph({
            children: parseInlineRuns(ordered[1].trim()),
            numbering: { reference: 'scenario-numbered-list', level: 0 },
            spacing: { after: 60 },
          }),
        );
        continue;
      }

      paragraphBuffer.push(line);
    }
    flushParagraph();
    return children;
  };

  const sections: import('docx').ISectionOptions[] = [
    {
      properties: {
        page: {
          margin: {
            top: 720,
            right: 720,
            bottom: 720,
            left: 720,
          },
        },
      },
      children: [
        // Document title
        new Paragraph({
          text: sanitizeDocxText(title),
          heading: HeadingLevel.TITLE,
          alignment: AlignmentType.CENTER,
          spacing: { after: 300 },
        }),

        // Summary table
        new Paragraph({
          text: 'Overview',
          heading: HeadingLevel.HEADING_1,
          spacing: { before: 200 },
        }),
        new Table({
          width: { size: 100, type: WidthType.PERCENTAGE },
          rows: [
            new TableRow({
              tableHeader: true,
              children: [
                new TableCell({
                  children: [new Paragraph('#')],
                  shading: { fill: 'FF7800' },
                }),
                new TableCell({
                  children: [
                    new Paragraph({
                      children: [
                        new TextRun({
                          text: 'Scenario',
                          bold: true,
                          color: 'FFFFFF',
                        }),
                      ],
                    }),
                  ],
                  shading: { fill: 'FF7800' },
                }),
              ],
            }),
            ...narratives.map(
              (n, i) =>
                new TableRow({
                  children: [
                    new TableCell({
                      children: [new Paragraph(String(i + 1))],
                    }),
                    new TableCell({
                      children: [new Paragraph(sanitizeDocxText(n.label))],
                    }),
                  ],
                }),
            ),
          ],
        }),

        // Page break before narratives
        new Paragraph({ children: [new PageBreak()] }),

        // Full narratives
        ...narratives.flatMap((n, idx) => {
          const children: import('docx').Paragraph[] = [
            new Paragraph({
              text: sanitizeDocxText(n.label),
              heading: HeadingLevel.HEADING_2,
              spacing: { before: idx === 0 ? 0 : 300 },
            }),
          ];

          children.push(...markdownParagraphs(n.content));

          if (n.coas && n.coas.length > 0) {
            children.push(
              new Paragraph({
                text: sanitizeDocxText(decisionsTitle),
                heading: HeadingLevel.HEADING_3,
                spacing: { before: 200, after: 80 },
              }),
            );
            for (const coa of n.coas) {
              if (coa.label) {
                children.push(
                  new Paragraph({
                    text: sanitizeDocxText(coa.label),
                    heading: HeadingLevel.HEADING_4,
                    spacing: { before: 160, after: 60 },
                  }),
                );
              }
              if (coa.risk) {
                children.push(
                  new Paragraph({
                    children: [new TextRun({ text: sanitizeDocxText(coa.risk), bold: true })],
                    spacing: { after: 60 },
                  }),
                );
              }
              for (const text of [coa.desc, coa.notes]) {
                if (text) children.push(...markdownParagraphs(text));
              }
              children.push(
                ...coa.decisions.map(
                  (d) =>
                    new Paragraph({
                      children: [
                        new TextRun({
                          text: `${sanitizeDocxText(d.label)}: `,
                          bold: true,
                        }),
                        new TextRun(sanitizeDocxText(d.values.join(', '))),
                      ],
                      bullet: { level: 0 },
                      spacing: { after: 60 },
                    }),
                ),
                ...(coa.warnings || []).map(
                  (w) =>
                    new Paragraph({
                      children: [
                        new TextRun({ text: sanitizeDocxText(w), italics: true }),
                      ],
                      spacing: { after: 60 },
                    }),
                ),
              );
              if (coa.reflection?.trim()) {
                children.push(
                  new Paragraph({
                    children: [
                      new TextRun({
                        text: sanitizeDocxText(options.reflectionTitle || 'Reflection'),
                        bold: true,
                      }),
                    ],
                    spacing: { before: 120, after: 60 },
                  }),
                  ...markdownParagraphs(coa.reflection),
                );
              }
            }
          }

          const boxChildren: Array<
            import('docx').Paragraph | import('docx').Table
          > = [];
          for (const boxTable of n.box || []) {
            if (boxTable.rows.length === 0) continue;
            if (boxTable.label) {
              boxChildren.push(
                new Paragraph({
                  text: sanitizeDocxText(boxTable.label),
                  heading: HeadingLevel.HEADING_3,
                  spacing: { before: 200, after: 80 },
                }),
              );
            } else {
              boxChildren.push(new Paragraph({ spacing: { before: 200 } }));
            }
            const maxValues = Math.max(
              1,
              ...boxTable.rows.map((r) => r.values.length),
            );
            boxChildren.push(
              new Table({
                width: { size: 100, type: WidthType.PERCENTAGE },
                rows: boxTable.rows.map((row) => {
                  const valueCells = row.values.map(
                    (v) =>
                      new TableCell({
                        children: [
                          new Paragraph({
                            children: [
                              new TextRun({
                                text: sanitizeDocxText(v.label),
                                bold: v.selected,
                                color: v.selected ? 'FFFFFF' : undefined,
                              }),
                            ],
                          }),
                        ],
                        shading: v.selected ? { fill: 'FF7800' } : undefined,
                      }),
                  );
                  const padding = maxValues - row.values.length;
                  if (padding > 0) {
                    valueCells.push(
                      new TableCell({
                        children: [new Paragraph('')],
                        columnSpan: padding,
                      }),
                    );
                  }
                  return new TableRow({
                    children: [
                      new TableCell({
                        children: [
                          new Paragraph({
                            children: [
                              new TextRun({
                                text: sanitizeDocxText(row.label),
                                bold: true,
                              }),
                            ],
                          }),
                        ],
                        shading: { fill: 'F2F2F2' },
                      }),
                      ...valueCells,
                    ],
                  });
                }),
              }),
            );
          }

          return [...children, ...boxChildren];
        }),
      ],
    },
  ];

  if (options.analysis) {
    const { comparisonChildren, comparisonPageProperties } = await import('./comparison-docx');
    sections.push({
      properties: comparisonPageProperties,
      children: await comparisonChildren(options.analysis, trafficLight),
    });
  }

  const doc = new Document({
    numbering: {
      config: [
        {
          reference: 'scenario-numbered-list',
          levels: [
            {
              level: 0,
              format: LevelFormat.DECIMAL,
              text: '%1.',
              alignment: AlignmentType.START,
              style: {
                paragraph: {
                  indent: { left: 720, hanging: 360 },
                },
              },
            },
          ],
        },
      ],
    },
    sections,
  });

  const blob = await Packer.toBlob(doc);
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/** Download the decision-support comparison (see `buildComparisonExport`) as a Word document. */
export const downloadComparisonDocx = async (data: ComparisonExport, filename = 'comparison.docx') => {
  const [{ Packer }, { comparisonDocument }] = await Promise.all([import('docx'), import('./comparison-docx')]);
  const blob = await Packer.toBlob(await comparisonDocument(data, trafficLight));
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export function fixMorphologicalBoxMarkdown(input: string): string {
  let output = input.trim();

  // Remove ```markdown, ```md, or similar code fences
  output = output.replace(/```(?:markdown|md)?\s*/gi, '```');
  // Remove leading/trailing triple backticks entirely
  output = output.replace(/^```[\s\S]*?```$/gm, (match) =>
    match.replace(/```/g, '').trim(),
  );

  // Normalize heading levels: convert any ## or ### at start to #
  output = output.replace(/^#{2,}\s*/gm, '# ');

  // Ensure list numbering starts correctly (fixes broken lists)
  // Replace bullets like "1)" or "1-" with "1."
  output = output.replace(/^(\d+)[\)\-]\s+/gm, '$1. ');

  // Normalize unordered lists: convert * or + to -
  output = output.replace(/^[\*\+]\s+/gm, '- ');

  // Fix indentation (ensure component values are indented under key drivers)
  // Adds two spaces before unordered list items not already indented
  output = output.replace(/^(-\s)/gm, '  $1');

  // Remove stray headings or explanations outside code block
  output = output
    .replace(/^>.*$/gm, '') // remove blockquotes
    .replace(/^#+\s*Example.*$/gim, '')
    .trim();

  // Remove extra blank lines (3+ in a row)
  output = output.replace(/\n{3,}/g, '\n\n');

  // Ensure there’s a single trailing newline
  if (!output.endsWith('\n')) output += '\n';

  return output;
}
